import { useTranslation } from 'react-i18next'
import React, { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import DigitalAssetActions from '../../../../modules/domain/digitalAsset/duck'
import DigitalAssetSelectors from '../../../../modules/domain/digitalAsset/selectors'
import CSS from 'csstype'
import { Button, Flex, Modal } from 'antd'
import { useAction } from '@agro-club/frontend-shared'
import DigitalAssetTable from './DigitalAssetTable'
import { Status, UploadCompanyType } from '../../../../types/entities'
import AuthSelectors from 'modules/domain/auth/selectors'
import DAFileTypeFilterSelect from 'views/components/DigitalAssets/Filters/DAFileTypeFilterSelect'
import DAFileSizeFilterSelect from 'views/components/DigitalAssets/Filters/DAFileSizeFilterSelect'
import DACompanyFilterSelect from 'views/components/DigitalAssets/Filters/DACompanyFilterSelect'
import DACreationDateFilterDatePicker from 'views/components/DigitalAssets/Filters/DACreationDateFilterDatePicker'
import DASearchInputFilter from 'views/components/DigitalAssets/Filters/DASearchInputFilter'
import { DigitalAsset } from 'modules/domain/digitalAsset/types'
import DropdownContainer from '../../../components/Generics/DropdownContainer'
import { useTableHeight } from '../../../../hooks/useTableHeight'
import { WarningOutlined } from '@ant-design/icons'

const assetActionStyles: CSS.Properties = {
  paddingRight: '1.5em',
}

const filterActionBarStyles: CSS.Properties = {
  paddingLeft: '1.5em',
}

const filterComponentsContainerStyles: CSS.Properties = {
  paddingTop: '1em',
  paddingBottom: '1em',
  paddingRight: '1.5em',
}

type DeletedDigitalAssetTableContentProps = {
  showAssetDetails: (asset: DigitalAsset) => void
}

const DeletedDigitalAssetsTableContent: React.FC<DeletedDigitalAssetTableContentProps> = ({
  showAssetDetails,
}): JSX.Element => {
  const [modal, contextHolder] = Modal.useModal()
  const { t } = useTranslation('digitalAsset')
  const listAction = useAction(DigitalAssetActions.listRequested)
  const uploadCompanyTypeSetAction = useAction(DigitalAssetActions.uploadCompanyTypeSet)
  const filterUpdated = useAction(DigitalAssetActions.filterUpdated)
  const restoreAction = useAction(DigitalAssetActions.restoreRequested)
  const permanentDeleteAction = useAction(DigitalAssetActions.permanentDeleteRequested)
  const isAdminOrProducer = useSelector(AuthSelectors.isAdminOrProducer)
  //scroll table variables
  const contentRef = useRef<HTMLDivElement>(null)
  //todo: this value has to be dynamically calculated to make the table 100% responsive
  const tableAdditionalContentSize = 265
  const tableHeight = useTableHeight(contentRef, tableAdditionalContentSize)

  const draftsDigitalAssets = useSelector(DigitalAssetSelectors.drafts)
  const selectedDigitalAssetDraftAction = useAction(DigitalAssetActions.selectedFileDraftRequested)

  // Set upload company type and fetch deleted digital assets on component mount
  useEffect(() => {
    uploadCompanyTypeSetAction(isAdminOrProducer ? UploadCompanyType.Producer : UploadCompanyType.Distributor)
    filterUpdated({ status: Status.Deleted })
    listAction({ page: 1 })
  }, [uploadCompanyTypeSetAction, filterUpdated, listAction, isAdminOrProducer])

  const handleDeleteFromTrash = useCallback(() => {
    if (draftsDigitalAssets && draftsDigitalAssets.length > 0) {
      const confirmContent = draftsDigitalAssets.map((asset) => {
        return <p key={asset.id}>{asset.name}</p>
      })
      modal.confirm({
        title: t('messages.permanentDeleteMessage'),
        content: confirmContent,
        icon: <WarningOutlined />,
        onOk: () => {
          const digitalAssetIds = draftsDigitalAssets.map((asset) => asset.id)
          permanentDeleteAction(digitalAssetIds)
          listAction({ page: 1 })
          selectedDigitalAssetDraftAction([])
        },
      })
    }
  }, [draftsDigitalAssets, permanentDeleteAction, listAction])

  const handleRestoreDigitalAsset = useCallback(() => {
    if (draftsDigitalAssets && draftsDigitalAssets.length > 0) {
      const confirmContent = draftsDigitalAssets.map((asset) => {
        return <p key={asset.id}>{asset.name}</p>
      })
      modal.confirm({
        title: t('messages.restoreMessage'),
        content: confirmContent,
        icon: <WarningOutlined />,
        cancelText: t('button.noConfirm'),
        okText: t('button.yesConfirm'),
        onOk: () => {
          const digitalAssetIds = draftsDigitalAssets.map((asset) => asset.id)
          restoreAction(digitalAssetIds)
          listAction({ page: 1 })
          selectedDigitalAssetDraftAction([])
        },
      })
    }
  }, [draftsDigitalAssets, restoreAction, listAction])

  return (
    <>
      {contextHolder}
      <Flex vertical gap="middle" ref={contentRef} flex={1}>
        <Flex vertical style={filterActionBarStyles}>
          <Flex justify="space-between" align="center" flex={1}>
            <Flex gap="middle" flex={1}>
              <DASearchInputFilter />
            </Flex>
            <Flex justify="flex-end" gap="middle" style={assetActionStyles} flex={1}>
              <Button
                size="large"
                color="danger"
                variant="filled"
                onClick={handleDeleteFromTrash}
                disabled={!draftsDigitalAssets || draftsDigitalAssets.length === 0}
              >
                {t('button.delete_trash')}
              </Button>
              <Button
                size="large"
                color="primary"
                variant="outlined"
                iconPosition="start"
                onClick={handleRestoreDigitalAsset}
                disabled={!draftsDigitalAssets || draftsDigitalAssets.length === 0}
              >
                {t('button.restore')}
              </Button>
            </Flex>
          </Flex>
          <DropdownContainer isShow={true}>
            <Flex style={filterComponentsContainerStyles} justify={'space-around'} wrap flex={1} gap="middle">
              <DAFileTypeFilterSelect />
              <DAFileSizeFilterSelect />
              <DACompanyFilterSelect />
              <DACreationDateFilterDatePicker />
            </Flex>
          </DropdownContainer>
        </Flex>
        <DigitalAssetTable height={tableHeight} setDigitalAssetDetail={showAssetDetails} canEditOrDelete={false} />
      </Flex>
    </>
  )
}

export default DeletedDigitalAssetsTableContent
